import {
  faArrowsRotate,
  faAward,
  faBook,
  faBuilding,
  faChartLine,
  faClipboardList,
  faComputer,
  faCrown,
  faFileArrowUp,
  faGauge,
  faGear,
  faMagnifyingGlass,
  faPalette,
  faPlug,
  faRocket,
  faScaleBalanced,
  faUserGroup,
  faUsers,
} from '@fortawesome/pro-light-svg-icons';

export const businessFaq = [
  {
    label: 'General',
    id: 'general',
    questions: [
      {
        icon: faBuilding,
        title: 'What is TryHackMe for Business?',
        answer:
          'TryHackMe for Business is a cybersecurity training and simulation platform designed for organizations and teams. It delivers practical, hands-on upskilling through guided labs, real-world challenges, and management tools that help you track and improve team capabilities.',
      },
      {
        icon: faScaleBalanced,
        title: 'How is TryHackMe for Business different from TryHackMe Premium?',
        answer:
          'TryHackMe Premium is for individual learners, while TryHackMe for Business offers enterprise-grade features like team management, centralized billing, progress dashboards, skill tracking, and performance insights. It is built to help companies scale cybersecurity capability development efficiently.',
      },
      {
        icon: faUsers,
        title: 'Who is TryHackMe for Business designed for?',
        answer:
          'It is tailored for organizations looking to strengthen cyber resilience - including SOC teams, security analysts, IT professionals, educators, and corporate training programs.',
      },
      {
        icon: faBook,
        title: 'What content and training does it include?',
        answer:
          'TryHackMe for Business provides full access to our entire library - from beginner-friendly introductions to advanced red and blue team exercises. Topics cover penetration testing, incident response, forensics, cloud, and more.',
      },
      {
        icon: faChartLine,
        title: "Can I measure my team's skills and progress?",
        answer:
          'Yes. Admins can view detailed analytics dashboards showing individual and team performance, skill progression, completion rates, and areas needing improvement.',
      },
      {
        icon: faComputer,
        title: 'What are SOC Simulators and how do they work?',
        answer:
          'SOC Simulators recreate real-world attack and defense scenarios in a controlled, virtual environment. They allow your analysts to detect, investigate, and respond to live incidents using authentic tools and data - building practical, on-the-job skills.',
      },
      {
        icon: faMagnifyingGlass,
        title: 'What are Threat Hunting Labs?',
        answer:
          'Our Threat Hunting Labs train teams to proactively identify and track malicious activity across simulated environments. They replicate real-world hunting exercises, teaching advanced detection, correlation, and intelligence analysis skills.',
      },
      {
        icon: faClipboardList,
        title: 'What are Tabletop Exercises?',
        answer:
          'Tabletop Exercises simulate high-stakes, strategic cyber incidents - such as ransomware outbreaks or data breaches - designed to test decision-making, collaboration, and response strategies across technical and leadership teams.',
      },
      {
        icon: faAward,
        title: 'Does TryHackMe for Business include assessments or certifications?',
        answer:
          'Yes - structured learning paths and challenge-based assessments help measure skill growth and verify readiness. Completion certificates can be used for internal training validation or certification prep.',
      },
      {
        icon: faPlug,
        title: 'Can we integrate TryHackMe with our existing systems?',
        answer:
          'Yes. Enterprise customers can enable Single Sign-On (SSO) and export reporting data, and we are expanding integration options with common LMS and HR systems.',
      },
      {
        icon: faArrowsRotate,
        title: 'How often is the content updated?',
        answer:
          'New labs, challenges, and learning paths are added weekly, covering emerging threats, tools, and techniques to ensure training remains current and realistic.',
      },
      {
        icon: faRocket,
        title: 'How can I get started or request a demo?',
        answer:
          "You can schedule a personalized demo or start a free trial directly through the TryHackMe for Business website to see how it fits your organization's cybersecurity training goals.",
      },
    ],
  },
  {
    label: 'Management Dashboard',
    id: 'management_dashboard',
    questions: [
      {
        icon: faGauge,
        title: 'What is the Management dashboard?',
        answer:
          'The Management dashboard is a centralised portal for the org admins that allows you to add, organise, and assign work to your team. It gives a clear overview of team activity, progress, and engagement - all in one place.',
      },
      {
        icon: faChartLine,
        title: 'What can I see on the dashboard?',
        answer:
          "You will find key insights at a glance, including active users, assignment completion rates, skill progression, and leaderboards helping you track your team's learning impact instantly.",
      },
      {
        icon: faFileArrowUp,
        title: 'Can I add a group of users directly?',
        answer:
          'Yes. The bulk upload option enables admins to import multiple users quickly using a CSV file. This is ideal for larger teams or organisations that need to onboard several learners at once. The system automatically processes the uploaded data, adds users to the platform, and allows the admin to assign them to groups or training content right away.',
      },
      {
        icon: faUserGroup,
        title: 'How does assigning users to groups and work operate?',
        answer:
          'When admins add users, they can immediately place them into existing or newly created groups. If users are added to an existing group, they will automatically receive the latest active assignment for that group. If users are not added to a group, the system will recommend a training module based on the skills the admin selected during setup. This ensures that every new learner is aligned with relevant work from the start.',
      },
      {
        icon: faGear,
        title: 'Can admins customise or skip parts of the flow?',
        answer:
          'Yes. The process is fully flexible, admins can choose which steps to complete. Assigning skills, groups, or modules is optional, allowing teams to tailor onboarding based on their workflow. Whether adding one user or a hundred, admins remain in full control of how users are introduced to their TryHackMe environment.',
      },
      {
        icon: faClipboardList,
        title: 'Is there a reporting functionality?',
        answer: 'Yes. We have a strong reporting option to help you get the insights you need regarding your team.',
      },
      {
        icon: faPalette,
        title: 'Can I create custom content to assign to my team?',
        answer:
          'Yes. In the content studio you will be able to either assign existing TryHackMe content, modify existing content or even create your own based on your training needs.',
      },
    ],
  },
  {
    label: 'Simulators & Tabletop exercises',
    id: 'thm_simulators_and_tabletop_exercises',
    questions: [
      {
        icon: faComputer,
        title: 'What are these products and who are they for?',
        answer:
          '<p style="white-space: pre-wrap; padding: 2rem;">• <a href="https://www.tryhackme.com/soc-sim" target="_blank" rel="noopener noreferrer" style="font-family: inherit; font-size: inherit; font-weight: inherit; line-height: inherit; text-decoration: none;">SOC Simulator:</a> Immersive triage training built around real attacks.\n\n• <a href="https://www.tryhackme.com/threat-hunting-sim" target="_blank" rel="noopener noreferrer" style="font-family: inherit; font-size: inherit; font-weight: inherit; line-height: inherit; text-decoration: none;">Threat Hunting Simulator:</a> Hands-on threat hunts to train attacker-style thinking.\n\n• <a href="https://www.tryhackme.com/ttx" target="_blank" rel="noopener noreferrer" style="font-family: inherit; font-size: inherit; font-weight: inherit; line-height: inherit; text-decoration: none;">TTX (Tabletop Exercises):</a> AI-guided tabletop exercises for real-world preparedness.</p>',
      },
      {
        icon: faChartLine,
        title: 'How is performance measured and reported?',
        answer:
          '• SOC Sim: Tracks metrics such as Mean Time to Resolve (MTTR), dwell time, false positive rate and awards points for correct triage, escalation and case reports.\n\n• Threat Hunting: A threat-hunting report is generated with performance insights (tactics & techniques identified, IoCs identified and detected compromised asset), accuracy scoring and key learning takeaways after you submit your attack chain.\n\n• TTX: Typically produces after-action findings and reports that document decisions, gaps and recommended improvements (format/content depends on exercise configuration).',
      },
      {
        icon: faCrown,
        title: 'Are these available for Free/Premium/Business users? Can I buy access separately?',
        answer:
          'Some scenarios/exercises will be available at Free/Premium tiers in a scaled/restricted form; full access is on the Business plan and cannot be purchased standalone.',
      },
      {
        icon: faBuilding,
        title: 'If I want to evaluate these for my organisation, who should I contact?',
        answer:
          'For Business/EDU evaluations, contact TryHackMe Sales/Customer Success via the Business/Education links in the product pages.',
      },
    ],
  },
] as const;
